import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';

import http from 'services/http';
import useArrastarLinhas from 'hook/arrastarLinhas';
import IconeMao from 'components/IconeMao';

import styles from '../styles.module.css';
import { alerta } from "../../../../components/Alert";

// Mapa do nome do módulo (modulos.nome) -> slug usado na chave
// `baixa_automatica_<slug>` em bancas.opcoes. DEVE espelhar o backend:
// config/modulos_baixa_automatica.php
const MODULO_SLUG = {
    'JB': 'tradicional',
    'Lototime': 'lototime',
    'Bolão': 'bolao',
    'Bolão de Dezena': 'bolao_dezena',
    'Seninha': 'seninha',
    'Sena Brasil': 'sena_brasil',
    'Rifa': 'rifa',
    // O módulo segue cadastrado como '2 para 500' por causa do app antigo;
    // 'Bilhetinho' fica aceito para bancas que ainda tenham o nome renomeado.
    '2 para 500': 'dois_quinhentos',
    'Bilhetinho': 'dois_quinhentos',
    'Raspadinha': 'raspadinha',
    'Fazendinha': 'fazendinha',
    // Modalidade nova; nome provisório (ver 2PRA500NEW.md no backend-jb).
    '2pra500new': 'dois_quinhentos_new',
};

// Rótulo amigável (override pontual). Demais usam o próprio modulo.nome.
const LABEL_OVERRIDE = {
    'JB': 'Tradicional',
    '2 para 500': '2 Pra 500',
    'Bilhetinho': '2 Pra 500',
};

// Módulos com explicação pontual (vai no title do select).
const SLUG_INFO = {
    'raspadinha': 'Quando ativo, bilhetes premiados são marcados como pagos automaticamente ao serem gerados.',
};

// Card do app (chave da ordem) -> nomes de módulo que liberam o card. DEVE espelhar
// o backend: app/Models/Modalidade/Support/CatalogoModalidades.php
const CARD_MODULOS = {
    'tradicional': ['JB'],
    'bichos-atrasados': ['JB'],
    'raspadinha': ['Raspadinha', 'Raspadinhas', 'Raspa', 'Scratch'],
    'bilhetinho': ['2 para 500', 'Bilhetinho'],
    'bolao-dezena': ['Bolão de Dezena'],
    'rifa': ['Rifa'],
    'bolao-pontos': ['Bolão', 'Bolão de pontos', 'Bolao pontos'],
    'bolao-placar': ['Bolão', 'Bolão de placar', 'Bolao placar'],
    'seninha': ['Seninha'],
    'quininha': ['Seninha'],
    'lotinha': ['Seninha'],
    'sena-brasil': ['Sena Brasil'],
    'quina-brasil': ['Sena Brasil'],
    'loto-brasil': ['Sena Brasil'],
    'esportivas': ['Esportivas'],
};

// Lototime não é configurável pela banca (a aba Dados já escondia o checkbox).
const MODULOS_OCULTOS = ['lototime'];

const chaveOpcao = (slug) => `baixa_automatica_${slug}`;

/**
 * Aba Categorias: numa tabela só, a ordem das modalidades na tela de venda do app
 * do cambista, o pagamento automático e o liga/desliga de cada módulo da banca.
 *
 * Eram três lugares (checkboxes na aba Dados, lista de "paga automaticamente" e
 * lista de ordem) e viraram um para a banca configurar tudo de uma vez.
 *
 * A ordem é por CARD do app e o resto é por MÓDULO — um módulo vira vários cards
 * ("Seninha" gera Seninha, Quininha e Lotinha). Por isso as linhas numeradas são os
 * cards, e "Paga automaticamente" e "Status" de cada linha são os do módulo dela:
 * mudar em Quininha muda em Seninha e Lotinha juntas. Módulo desligado não vira card
 * (a API nem devolve), e módulo sem card próprio não tem o que ordenar: esses entram
 * no fim, com "—" na posição, só para pagar/ligar.
 *
 * Enquanto a banca não salvar a ordem ela segue o padrão global da Setebit e
 * acompanha as mudanças que a central fizer. Ao salvar, passa a ter ordem própria e
 * ignora o padrão — "Restaurar ordem padrão" desfaz isso.
 */
export default function ConfiguracaoCategorias() {
    // módulos da banca como vêm da API: [{ id, nome, status }]
    const [modulos, setModulos] = useState([]);
    // { [slug]: 0 | 1 }
    const [valores, setValores] = useState({});
    // cards da tela de venda, na ordem em que o vendedor vai vê-los: [{ chave, nome }]
    const [ordem, setOrdem] = useState([]);
    const [ordemDisponivel, setOrdemDisponivel] = useState(false);
    const [personalizada, setPersonalizada] = useState(false);
    const [carregaPost, setCarregaPost] = useState(false);

    const carregar = async () => {
        try {
            const [resModulos, resOpcoes] = await Promise.all([
                http.get('/bancas/modulos'),
                http.get('/bancas/opcoes'),
            ]);

            const lista = (resModulos.data.data || [])
                .filter((m) => !MODULOS_OCULTOS.includes(m.nome?.toLowerCase()));

            const opcoes = resOpcoes.data.data.opcoes || {};

            const estado = {};
            lista.forEach(({ nome }) => {
                const slug = MODULO_SLUG[nome];

                if (slug) {
                    // Number('0') -> 0, Number('1')/Number(true) -> 1, ausente -> NaN (desligado)
                    estado[slug] = Number(opcoes[chaveOpcao(slug)]) ? 1 : 0;
                }
            });

            setModulos(lista);
            setValores(estado);
        } catch (e) {
            alerta(e.response?.data || { message: 'Erro ao carregar as configurações.' });
        }

        // A ordem vem de outro endpoint, e num try próprio: se ela não responder — API
        // ainda sem a rota, ou usuário sem "Listar modalidade" — a aba continua
        // servindo para pagamento e status, com todos os módulos na parte sem posição.
        try {
            const resOrdem = await http.get('/bancas/modalidades/ordem');
            const { modalidades, personalizada: propria } = resOrdem.data.data;

            setOrdem(modalidades || []);
            setPersonalizada(!!propria);
            setOrdemDisponivel(true);
        } catch (e) {
            setOrdem([]);
            setOrdemDisponivel(false);
        }
    };

    useEffect(() => {
        carregar();
    }, []);

    const moduloDoCard = (chave) => {
        const nomes = CARD_MODULOS[chave] || [];

        return modulos.find((m) => nomes.includes(m.nome)) || null;
    };

    // Módulos que não aparecem em nenhum card da ordem: desligados, ou sem card próprio.
    const modulosSemCard = modulos.filter(
        (modulo) => !ordem.some(({ chave }) => (CARD_MODULOS[chave] || []).includes(modulo.nome))
    );

    const setValor = (slug, valor) => {
        setValores((atual) => ({ ...atual, [slug]: Number(valor) }));
    };

    const alternarStatus = (id) => {
        setModulos((atual) => atual.map((modulo) => (
            modulo.id === id ? { ...modulo, status: modulo.status === 1 ? 0 : 1 } : modulo
        )));
    };

    /**
     * Tira o card de onde ele está e encaixa na posição de destino, empurrando o
     * resto. É isso que faz o número das outras linhas se ajustar sozinho.
     */
    const moverPara = (origem, destino) => {
        if (origem === null || destino === null || origem === destino) {
            return;
        }

        setOrdem((atual) => {
            const lista = [...atual];
            const [movido] = lista.splice(origem, 1);

            lista.splice(destino, 0, movido);

            return lista;
        });
    };

    const arraste = useArrastarLinhas(moverPara);

    /**
     * Move a linha para a posição digitada. Reordenar na hora (em vez de só guardar o
     * número) é o que deixa a tela mostrar o resultado antes de salvar.
     */
    const irParaPosicao = (indice, valor) => {
        const posicao = parseInt(valor, 10);

        if (!posicao || posicao < 1 || posicao > ordem.length) {
            return;
        }

        moverPara(indice, posicao - 1);
    };

    /**
     * A ordem vai primeiro, ainda com a lista de cards que a tela carregou; depois
     * pagamento e status. Nessa ordem, card de módulo que acabou de ser ligado entra
     * na posição padrão, e o de módulo desligado simplesmente some do app. No fim a
     * tela recarrega do servidor — se algo falhar no meio, a banca vê o que ficou
     * gravado, e não o que ela montou na tela.
     *
     * O menu lateral (seção CATEGORIAS) sai dos módulos do perfil guardado no
     * navegador, por isso o /perfil é relido depois de salvar, como a aba Dados fazia.
     */
    const mandaAlteracoes = async () => {
        try {
            setCarregaPost(true);

            if (ordemDisponivel && ordem.length > 0) {
                await http.put('/bancas/modalidades/ordem', {
                    ordem: ordem.map(({ chave }, indice) => ({ chave, posicao: indice + 1 })),
                });
            }

            const formData = new FormData();
            Object.keys(valores).forEach((slug) => {
                formData.append(chaveOpcao(slug), valores[slug] ? 1 : 0);
            });
            formData.append('modulos', JSON.stringify(modulos.map(({ id, status }) => ({ id, status }))));

            const res = await http.post('/bancas/opcoes', formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            const perfil = await http.get('/perfil');
            localStorage.setItem('usuario', JSON.stringify(perfil.data.data.usuario));

            alerta(res.data);
            await carregar();
        } catch (error) {
            alerta(error.response?.data || { message: 'Erro ao salvar.' });
            await carregar();
        } finally {
            setCarregaPost(false);
        }
    };

    const perguntaRestaurar = () => {
        Swal.fire({
            title: 'Atenção!',
            text: 'A ordem que você montou será apagada e as modalidades voltam a aparecer na ordem padrão do sistema. Deseja continuar?',
            type: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            reverseButtons: true,
            confirmButtonText: 'Confirmar',
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (result.value) {
                restaurarPadrao();
            }
        });
    };

    const restaurarPadrao = async () => {
        try {
            setCarregaPost(true);

            const res = await http.delete('/bancas/modalidades/ordem');

            alerta(res.data);
            await carregar();
        } catch (e) {
            alerta(e.response?.data || { message: 'Erro ao restaurar a ordem padrão.' });
        } finally {
            setCarregaPost(false);
        }
    };

    const colunaPaga = (modulo) => {
        const slug = modulo && MODULO_SLUG[modulo.nome];

        if (!slug) {
            return <span className="text-muted">—</span>;
        }

        return (
            <select
                className={`form-control ${styles.selectPaga}`}
                title={SLUG_INFO[slug]}
                disabled={carregaPost}
                value={valores[slug] ?? 0}
                onChange={(e) => setValor(slug, e.target.value)}
            >
                <option value={1}>Sim</option>
                <option value={0}>Não</option>
            </select>
        );
    };

    const colunaStatus = (modulo) => {
        if (!modulo) {
            return <span className="text-muted">—</span>;
        }

        const ativo = modulo.status === 1;

        return (
            <button
                type="button"
                className={`btn ${ativo ? 'btn-success' : 'btn-danger'} ${styles.botaoStatus}`}
                title={ativo ? 'Clique para desligar' : 'Clique para ligar'}
                disabled={carregaPost}
                onClick={() => alternarStatus(modulo.id)}
            >
                {ativo ? 'Ativo' : 'Inativo'}
            </button>
        );
    };

    const classeAlvo = (indice) => {
        if (arraste.alvo !== indice || arraste.arrastando === null || arraste.arrastando === indice) {
            return '';
        }

        return indice > arraste.arrastando ? styles.alvoAbaixo : styles.alvoAcima;
    };

    return (
        <div className={styles.conteudoPrincipal}>

            {ordemDisponivel && (personalizada ? (
                <div className="alert alert-info">
                    Esta banca usa uma ordem própria. Mudanças na ordem padrão do sistema
                    não afetam mais o seu aplicativo.
                </div>
            ) : (
                <div className="alert alert-secondary">
                    Esta banca está usando a ordem padrão do sistema. Ao salvar, ela passa
                    a ter uma ordem própria.
                </div>
            ))}

            <div className={`table-responsive ${styles.tabelaCategorias}`}>
                <table className="table table-striped">
                    <thead>
                    <tr>
                        <th className={styles.colunaPosicao}>Posição</th>
                        <th className={styles.colunaAlca} />
                        <th>Modalidade</th>
                        <th className={styles.colunaPaga}>Paga automaticamente</th>
                        <th className={styles.colunaStatus}>Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    {ordem.map(({ chave, nome }, indice) => {
                        const modulo = moduloDoCard(chave);

                        return (
                            <tr
                                key={chave}
                                className={[
                                    arraste.arrastando === indice ? styles.arrastando : '',
                                    classeAlvo(indice),
                                ].join(' ').trim()}
                                {...arraste.linha(indice)}
                            >
                                <td className={styles.colunaPosicao}>
                                    <input
                                        type="number"
                                        min="1"
                                        max={ordem.length}
                                        className="form-control"
                                        disabled={carregaPost}
                                        value={indice + 1}
                                        onChange={(e) => irParaPosicao(indice, e.target.value)}
                                    />
                                </td>

                                <td className={styles.colunaAlca}>
                                    <span
                                        className={styles.alca}
                                        title="Arraste para mudar a posição"
                                        {...arraste.alca(indice, carregaPost)}
                                    >
                                        <IconeMao/>
                                    </span>
                                </td>

                                <td>{nome}</td>
                                <td className={styles.colunaPaga}>{colunaPaga(modulo)}</td>
                                <td className={styles.colunaStatus}>{colunaStatus(modulo)}</td>
                            </tr>
                        );
                    })}

                    {modulosSemCard.map((modulo) => (
                        <tr key={`modulo-${modulo.id}`}>
                            <td className={styles.colunaPosicao}>
                                <input type="text" className="form-control" value="—" disabled readOnly/>
                            </td>

                            <td className={styles.colunaAlca}>
                                <span className={`${styles.alca} ${styles.alcaInativa}`}>
                                    <IconeMao/>
                                </span>
                            </td>

                            <td>{LABEL_OVERRIDE[modulo.nome] || modulo.nome}</td>
                            <td className={styles.colunaPaga}>{colunaPaga(modulo)}</td>
                            <td className={styles.colunaStatus}>{colunaStatus(modulo)}</td>
                        </tr>
                    ))}

                    {ordem.length === 0 && modulosSemCard.length === 0 && (
                        <tr>
                            <td colSpan={5} className="text-muted">Nenhuma modalidade para configurar.</td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            <div className={styles.rodapeCategorias}>
                <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => perguntaRestaurar()}
                    disabled={carregaPost || !personalizada}
                    title={personalizada ? '' : 'A banca já está usando a ordem padrão'}
                >
                    Restaurar ordem padrão
                </button>

                <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => mandaAlteracoes()}
                    disabled={carregaPost || (modulos.length === 0 && ordem.length === 0)}
                >
                    Salvar
                </button>
            </div>

        </div>
    );
}
